import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePlanningStore, type CompactionMode } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import { useDispoAtelierStore } from "@store/dispoAtelierStore";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { planningArrowsAllowed } from "@utils/planningArrowKeys";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchAlgolia from "@components/ui/search/SearchAlgolia";
import MiniCalendar from "@components/ui/MiniCalendar";
import FullscreenToggle from "@components/ui/buttons/FullscreenToggle";
import { AvailabilityChart } from "./AvailabilityChart";
import { computeWeeklyAvailability } from "./computeAvailability";
import { CategoryFilters } from "./CategoryFilters";
import { TimeGridWithAppointments } from "./TimeGridWithAppointments";
import { WeekTimeGridReception } from "./WeekTimeGridReception";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { searchOrdresFull } from "@services/algolia";
import { ordresApi } from "@services/api/ordres";
import { showNotification } from "context/notification.service";
import { atelierApi } from "@services/api/atelier";
import OrdreModal from "@components/modals/OrdreModal";
import NouveauRDVModal from "@components/modals/NouveauRDVModal";
import type { Ordre } from "types/ordre";
import type { WorkshopJob } from "types/garage/atelier";
import { getMonday, toISO } from "@store/disponibiliteStore";
import hintDragIcon from "@assets/icons/hint-drag.svg";
import hintDropGif from "@assets/gifs/hint-drop.gif";

function tsToDateStr(ts?: { seconds: number }): string | null {
  if (!ts) return null;
  const d = new Date(ts.seconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function ReceptionScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const fetchAppointments = usePlanningStore((s) => s.fetchAppointments);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const fetchBlocks = useDispoAtelierStore((s) => s.fetchBlocks);

  useEffect(() => {
    if (orgData?.id) {
      fetchAppointments(orgData.id);
      fetchAtelierData(orgData.id);
      fetchBlocks(orgData.id);
    }
  }, [orgData?.id]);

  const selectedDate = usePlanningStore((s) => s.selectedDate);
  const setSelectedDate = usePlanningStore((s) => s.setSelectedDate);
  const viewMode = usePlanningStore((s) => s.viewMode);
  const setViewMode = usePlanningStore((s) => s.setViewMode);
  const compaction = usePlanningStore((s) => s.compaction);
  const setCompaction = usePlanningStore((s) => s.setCompaction);
  const isRefreshing = usePlanningStore((s) => s.isRefreshing);
  const [algoliaQuery, setAlgoliaQuery] = useState("");
  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showNewRdv, setShowNewRdv] = useState(false);

  const blocks = useDispoAtelierStore((s) => s.blocks);

  // Fullscreen: hide app chrome (nav, page title, calendar/dispo sidebar) and let the
  // grid fill the whole viewport. Exit with the button or the Escape key. Same
  // behaviour as the atelier planning — see AtelierScreen.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // Fetch jobs covering the 3-week chart range (independent of atelier store's single-week fetch)
  const [chartJobs, setChartJobs] = useState<WorkshopJob[]>([]);
  const chartRangeKey = useMemo(() => {
    const monday = getMonday(selectedDate);
    const friday3 = new Date(monday);
    friday3.setDate(monday.getDate() + 18); // 3 weeks − 1 day (Mon+18=Fri of week 3)
    return { date_from: toISO(monday), date_to: toISO(friday3) };
  }, [selectedDate]);

  useEffect(() => {
    if (!orgData?.id) return;
    atelierApi
      .listJobs({ org_id: orgData.id, date_from: chartRangeKey.date_from, date_to: chartRangeKey.date_to, limit: 1000 })
      .then(setChartJobs)
      .catch(() => {});
  }, [orgData?.id, chartRangeKey.date_from, chartRangeKey.date_to]);

  // Arrow key navigation: ±7 days in week view, ±1 day in day view
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (!planningArrowsAllowed(e)) return;
      const { viewMode, selectedDate, setSelectedDate } = usePlanningStore.getState();
      e.preventDefault();
      const delta = e.key === "ArrowLeft" ? -(viewMode === "semaine" ? 7 : 1) : (viewMode === "semaine" ? 7 : 1);
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + delta);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      setSelectedDate(iso);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const weeklyAvailability = useMemo(
    () => computeWeeklyAvailability(selectedDate, chartJobs, blocks),
    [selectedDate, chartJobs, blocks],
  );

  return (
    <div
      className={`flex flex-col overflow-auto h-screen ${fullscreen ? "fixed inset-0 z-[9998]" : ""}`}
      style={{ background: "#FAFAFA" }}
    >
      <div className="flex flex-col h-fit">
        {/* Title (hidden in fullscreen to give the grid maximum height) */}
        {!fullscreen && (
          <div
            className="flex flex-col px-4 lg:px-10 pt-8"
          >
            <Text1>Planning réception</Text1>
            <Text2>Planifiez vos rendez-vous clients ici.</Text2>
          </div>
        )}

        <div
          className={`flex flex-row flex-1 px-4 lg:px-10 ${fullscreen ? "pt-3" : "pt-5"}`}
          style={{
            gap: 30,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Left column (calendar + dispo charts) — hidden in fullscreen */}
          <div
            className="flex flex-col"
            style={{
              width: 196,
              flexShrink: 0,
              gap: 20,
              overflowY: "auto",
              paddingBottom: 30,
              display: fullscreen ? "none" : "flex",
            }}
          >
            <MiniCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              weekHighlight={viewMode === "semaine"}
              showNavButtons
            />
            <div className="flex flex-col" style={{ gap: 15 }}>
              <span
                style={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#A1A1A1",
                }}
              >
                Dispo. atelier(s)
              </span>
              {weeklyAvailability.map((week, i) => (
                <AvailabilityChart key={i} week={week} />
              ))}
            </div>
          </div>

          {/* Right column */}
          <div
            className="flex flex-col flex-1"
            style={{
              gap: 10,
              minWidth: 0,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <div className="flex flex-row items-center" style={{ gap: 10 }}>
              <div style={{ width: 400, flexShrink: 0 }}>
                <SearchAlgolia
                  value={algoliaQuery}
                  setValue={setAlgoliaQuery}
                  placeholder="Rechercher un ordre..."
                  classNameCustom="w-full"
                  onSearch={async (query) => {
                    if (!orgData?.id) return [];
                    return searchOrdresFull(query, orgData.id);
                  }}
                  onAction={(option) => {
                    if (typeof option === "object" && option.value) {
                      ordresApi.get(option.value).then((ordre) => {
                        const date = tsToDateStr(ordre.reception_date) || tsToDateStr(ordre.created_at);
                        if (date) setSelectedDate(date);
                        setSelectedOrdre(ordre);
                      }).catch((err) => {
                        // L'ordre peut avoir été supprimé alors qu'il figure encore
                        // dans l'index de recherche Algolia → 404. On informe sans crasher.
                        if (err?.response?.status === 404) {
                          showNotification({
                            message: "Cet ordre a été supprimé et n'est plus disponible.",
                            type: "warning",
                          });
                        } else {
                          showNotification({
                            message: "Impossible d'ouvrir cet ordre.",
                            type: "error",
                          });
                        }
                      });
                      return true;
                    }
                  }}
                />
              </div>
              <ChunkyTabs
                activeTab={viewMode}
                tabs={[
                  { value: "jour", label: "Jour" },
                  { value: "semaine", label: "Semaine" },
                ]}
                setActiveTab={(tab) => setViewMode(tab as "jour" | "semaine")}
              />
              <CompactionSelector compaction={compaction} setCompaction={setCompaction} />
              <FullscreenToggle fullscreen={fullscreen} onToggle={() => setFullscreen((f) => !f)} />
            </div>
            <CategoryFilters />
            <div
              className={`h-0.5 rounded-full transition-opacity duration-300 ${isRefreshing ? "opacity-100" : "opacity-0"}`}
              style={{ background: "linear-gradient(90deg, #3b82f6, #60a5fa, #3b82f6)", backgroundSize: "200% 100%", animation: isRefreshing ? "shimmer 1.2s ease-in-out infinite" : "none" }}
            />
            {viewMode === "jour" ? (
              <TimeGridWithAppointments />
            ) : (
              <WeekTimeGridReception onDragStateChange={setIsDragging} />
            )}
          </div>
        </div>
      </div>
      <ReceptionHints isDragging={isDragging} viewMode={viewMode} />

      {/* Floating "Prendre RDV" — opens the same booking flow as a client, with
          internal client search (immat / nom / téléphone).
          Décalé à right-[104px] pour ne pas recouvrir le bouton Gleap (bas-droite). */}
      <button
        onClick={() => setShowNewRdv(true)}
        className="fixed bottom-6 right-[104px] z-40 flex items-center gap-2 rounded-full px-5 py-3 shadow-xl hover:scale-105 transition-transform"
        style={{ background: "#5C6BFF" }}
      >
        <span className="text-h2 font-medium text-white">+ Prendre RDV</span>
      </button>

      {showNewRdv && (
        <NouveauRDVModal
          onClose={() => setShowNewRdv(false)}
          onSuccess={() => {
            if (orgData?.id) fetchAppointments(orgData.id, true);
            setShowNewRdv(false);
          }}
        />
      )}

      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={(updated) => setSelectedOrdre(updated)}
          onDelete={isOwnerOrAdmin(userData) ? () => setSelectedOrdre(null) : undefined}
        />
      )}
    </div>
  );
}

// --- Compaction selector ---

const COMPACTION_OPTIONS: { value: CompactionMode; icon: React.ReactNode; title: string }[] = [
  {
    value: "compact",
    title: "Compact",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="3" width="14" height="2" rx="1" fill="currentColor" />
        <rect x="2" y="8" width="14" height="2" rx="1" fill="currentColor" />
        <rect x="2" y="13" width="14" height="2" rx="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "normal",
    title: "Normal",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="2" width="14" height="3" rx="1" fill="currentColor" />
        <rect x="2" y="7.5" width="14" height="3" rx="1" fill="currentColor" />
        <rect x="2" y="13" width="14" height="3" rx="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: "spread",
    title: "Large",
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="1" width="14" height="4" rx="1" fill="currentColor" />
        <rect x="2" y="7" width="14" height="4" rx="1" fill="currentColor" />
        <rect x="2" y="13" width="14" height="4" rx="1" fill="currentColor" />
      </svg>
    ),
  },
];

function CompactionSelector({ compaction, setCompaction }: { compaction: CompactionMode; setCompaction: (m: CompactionMode) => void }) {
  return (
    <div className="flex bg-gray-100 rounded-lg p-0.5" title="Taille des lignes">
      {COMPACTION_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => setCompaction(opt.value)}
          title={opt.title}
          className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${
            compaction === opt.value
              ? "bg-white shadow text-neutral-900"
              : "text-neutral-400 hover:text-neutral-600"
          }`}
        >
          {opt.icon}
        </button>
      ))}
    </div>
  );
}

// --- Onboarding hints ---

function HintBubble({ children, visible }: { children: React.ReactNode; visible: boolean }) {
  return (
    <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}>
      <div className="bg-white text-neutral-800 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-2xl border border-neutral-200">
        {children}
      </div>
    </div>
  );
}

function KeyIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-neutral-100 border border-neutral-300 font-mono text-sm font-semibold">
      {children}
    </span>
  );
}

const HINT_SEQUENCE_IDLE = [
  { id: "arrows", ms: 3000, content: (
    <>
      <div className="flex items-center gap-1"><KeyIcon>&larr;</KeyIcon><KeyIcon>&rarr;</KeyIcon></div>
      <span className="text-h3 font-medium whitespace-nowrap">Utilisez les flèches pour naviguer</span>
    </>
  )},
  { id: "drag", ms: 3000, content: (
    <>
      <img src={hintDragIcon} alt="" className="w-6 h-6" />
      <span className="text-h3 font-medium whitespace-nowrap">Glissez-déposez les rendez-vous pour les déplacer</span>
    </>
  )},
] as const;

const HINT_SEQUENCE_DRAGGING = [
  { id: "dragArrows", ms: 3000, content: (
    <>
      <div className="flex items-center gap-1"><KeyIcon>&larr;</KeyIcon><KeyIcon>&rarr;</KeyIcon></div>
      <span className="text-h3 font-medium whitespace-nowrap">Naviguez entre les semaines avec le rendez-vous</span>
    </>
  )},
  { id: "drop", ms: 3000, content: (
    <>
      <img src={hintDropGif} alt="" className="w-6 h-6" />
      <span className="text-h3 font-medium whitespace-nowrap">Relâchez pour placer le rendez-vous</span>
    </>
  )},
] as const;

function ReceptionHints({ isDragging, viewMode }: { isDragging: boolean; viewMode: string }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const isDraggingRef = useRef(isDragging);
  const playedIdleRef = useRef(false);
  const playedDragRef = useRef(false);

  const sequence = isDragging ? HINT_SEQUENCE_DRAGGING : HINT_SEQUENCE_IDLE;

  useEffect(() => {
    if (isDragging === isDraggingRef.current) return;
    isDraggingRef.current = isDragging;
    const alreadyPlayed = isDragging ? playedDragRef.current : playedIdleRef.current;
    if (alreadyPlayed) {
      setStepIndex(sequence.length);
      return;
    }
    setStepIndex(0);
    setVisible(true);
  }, [isDragging]);

  useEffect(() => {
    if (stepIndex >= sequence.length) {
      if (isDragging) playedDragRef.current = true;
      else playedIdleRef.current = true;
      return;
    }
    setVisible(true);
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => setStepIndex((i) => i + 1), 300);
    }, sequence[stepIndex].ms);
    return () => clearTimeout(timer);
  }, [stepIndex, sequence]);

  if (stepIndex >= sequence.length) return null;

  return <HintBubble visible={visible}>{sequence[stepIndex].content}</HintBubble>;
}
